import type { FormDefinitionProblemModel } from './form-definition';
import type {
	FormFieldDefinitionModel,
	FormSelectOptionModel,
	FormShowWhenModel,
} from './form-types';

/**
 * PD-16730 — conditional questions: a question can be set to show only when an earlier
 * choice question has one of a list of answers ("Business DBA Name" only after "Yes", a
 * billing address only after "No", a "please specify" box only after "Other").
 *
 * Closed on purpose, like action conditions: one other question, and a list of its
 * answers. Chains are allowed (a question may depend on one that is itself conditional),
 * and a hidden question hides everything that depends on it. The controlling question
 * must come earlier in the form, so a loop cannot be saved.
 *
 * The SERVER decides what is shown: the validator (form-validator.ts) never requires a
 * hidden question and drops its answer, so a page that showed it anyway cannot store it.
 * The storefront renderer and the website forms code
 * (modules/websites/src/site-blocks/workd-forms.ts, which cannot import this file) read
 * the same rule the same way; a parity test runs one table of cases through both.
 *
 * Pure and dependency-free, so it runs unchanged in a browser and in Node.
 */

/** The kinds of question whose answer can show or hide another one. */
export const FORM_SHOW_WHEN_CONTROLLER_TYPES: ReadonlyArray<string> = [
	'select',
	'multiselect',
	'boolean',
];

/** A yes/no question's answers, as a rule names them. */
export const FORM_SHOW_WHEN_YES = 'true';
export const FORM_SHOW_WHEN_NO = 'false';

export function isFormShowWhenController(
	field: Pick<FormFieldDefinitionModel, 'fieldType'>,
): boolean {
	return FORM_SHOW_WHEN_CONTROLLER_TYPES.includes(field.fieldType);
}

/**
 * The answers a controlling question offers, in its own order: a choice question's
 * choices, or Yes and No. Any other kind of question offers none.
 */
export function formShowWhenAnswers(
	field: Pick<FormFieldDefinitionModel, 'fieldType' | 'selectOptions'>,
): Array<FormSelectOptionModel> {
	if (field.fieldType === 'boolean') {
		return [
			{ value: FORM_SHOW_WHEN_YES, label: 'Yes' },
			{ value: FORM_SHOW_WHEN_NO, label: 'No' },
		];
	}
	if (field.fieldType === 'select' || field.fieldType === 'multiselect') {
		return [...(field.selectOptions ?? [])];
	}
	return [];
}

function answerText(value: unknown): string | null {
	if (typeof value === 'string') {
		return value.trim();
	}
	if (typeof value === 'number' && Number.isFinite(value)) {
		return String(value);
	}
	if (typeof value === 'boolean') {
		return value ? FORM_SHOW_WHEN_YES : FORM_SHOW_WHEN_NO;
	}
	return null;
}

/**
 * A rule as stored or sent, reduced to its shape: the controlling question's key and its
 * answers as distinct, non-empty strings. Anything that is not a rule (a missing or empty
 * key, not an object) is "always show", null. What the rule MEANS (the question exists,
 * comes earlier, offers those answers) is checked by checkFormFieldShowWhen.
 */
export function normalizeFormShowWhen(raw: unknown): FormShowWhenModel | null {
	if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) {
		return null;
	}
	const record = raw as Record<string, unknown>;
	const fieldKey =
		typeof record['fieldKey'] === 'string' ? record['fieldKey'].trim() : '';
	if (fieldKey === '') {
		return null;
	}
	const values: Array<string> = [];
	const rawValues: unknown = record['values'];
	for (const value of Array.isArray(rawValues) ? rawValues : []) {
		const text = answerText(value);
		if (text != null && text !== '' && !values.includes(text)) {
			values.push(text);
		}
	}
	return { fieldKey, values };
}

/**
 * The fields with each rule's answers put in its controlling question's order (answers it
 * does not offer stay last, as given, for the definition check to name). Ticking answers
 * in a different order than last time is then no change at all, so a save does not write
 * a version nobody needed.
 */
export function orderFormShowWhenValues<T extends FormFieldDefinitionModel>(
	fields: ReadonlyArray<T>,
): Array<T> {
	const byKey = new Map(fields.map((field) => [field.fieldKey, field]));
	return fields.map((field) => {
		const rule = field.showWhen ?? null;
		const controller = rule != null ? byKey.get(rule.fieldKey) : undefined;
		if (rule == null || controller == null) {
			return field;
		}
		const order = formShowWhenAnswers(controller).map((answer) => answer.value);
		const known = order.filter((value) => rule.values.includes(value));
		const unknown = rule.values.filter((value) => !order.includes(value));
		const values = [...known, ...unknown];
		return values.every((value, index) => rule.values[index] === value)
			? field
			: { ...field, showWhen: { fieldKey: rule.fieldKey, values } };
	});
}

/**
 * A controlling question's answer as the rule reads it: the answers chosen. A yes/no
 * question left unanswered counts as No (a switch that shows off means no); a
 * multiple-answer question gives every answer ticked.
 */
export function formShowWhenChosen(
	controller: Pick<FormFieldDefinitionModel, 'fieldType'>,
	answer: unknown,
): Array<string> {
	if (controller.fieldType === 'boolean') {
		const yes =
			answer === true ||
			answer === 1 ||
			answer === '1' ||
			answer === FORM_SHOW_WHEN_YES ||
			answer === 'on';
		return [yes ? FORM_SHOW_WHEN_YES : FORM_SHOW_WHEN_NO];
	}
	const items: Array<unknown> = Array.isArray(answer) ? answer : [answer];
	return items
		.map(answerText)
		.filter((text): text is string => text != null && text !== '');
}

/** Whether the controlling question's answer is one the rule names. */
export function formShowWhenMatches(
	rule: Pick<FormShowWhenModel, 'values'>,
	controller: Pick<FormFieldDefinitionModel, 'fieldType'>,
	answer: unknown,
): boolean {
	return formShowWhenChosen(controller, answer).some((value) =>
		rule.values.includes(value),
	);
}

/**
 * Whether a question shows on every submit that can be accepted, rule or not: its rule
 * names every answer its controlling question offers, and that question must be answered
 * (it is required, or a yes/no question, which reads No when left alone) and itself always
 * shows. PD-17590's "Do you have an account with us?" puts every shared question behind a
 * rule like that, so a required email behind it is still required on every submit.
 */
export function formFieldAlwaysShown(
	fields: ReadonlyArray<
		Pick<
			FormFieldDefinitionModel,
			'fieldKey' | 'fieldType' | 'selectOptions' | 'isRequired' | 'showWhen'
		>
	>,
	fieldKey: string,
): boolean {
	const byKey = new Map(fields.map((field) => [field.fieldKey, field]));
	const visiting = new Set<string>();
	const shown = (key: string): boolean => {
		const rule = normalizeFormShowWhen(byKey.get(key)?.showWhen ?? null);
		if (rule == null) {
			return true;
		}
		const controller = byKey.get(rule.fieldKey);
		if (controller == null || visiting.has(key)) {
			return false;
		}
		const required: unknown = controller.isRequired;
		const answered =
			controller.fieldType === 'boolean' || required === true || required === 1;
		const answers = formShowWhenAnswers(controller);
		if (
			!answered ||
			answers.length === 0 ||
			!answers.every((answer) => rule.values.includes(answer.value))
		) {
			return false;
		}
		visiting.add(key);
		const result = shown(controller.fieldKey);
		visiting.delete(key);
		return result;
	};
	return shown(fieldKey);
}

/**
 * The questions hidden by their rules for these answers. A question shows when it has no
 * rule, or when the question it depends on shows AND has one of the chosen answers; so
 * hiding a question hides everything that depends on it.
 *
 * Forgiving, since it reads stored versions it never checked: a rule naming a question
 * that is not on the form, or a loop (which the definition check refuses), leaves the
 * question shown rather than silently dropping what the visitor typed.
 */
export function formHiddenFieldKeys(
	fields: ReadonlyArray<
		Pick<FormFieldDefinitionModel, 'fieldKey' | 'fieldType' | 'showWhen'>
	>,
	answers: Readonly<Record<string, unknown>>,
): Set<string> {
	const byKey = new Map(fields.map((field) => [field.fieldKey, field]));
	const shown = new Map<string, boolean>();
	const visiting = new Set<string>();

	const isShown = (key: string): boolean => {
		const known = shown.get(key);
		if (known != null) {
			return known;
		}
		const field = byKey.get(key);
		const rule = normalizeFormShowWhen(field?.showWhen ?? null);
		const controller = rule != null ? byKey.get(rule.fieldKey) : undefined;
		if (
			field == null ||
			rule == null ||
			controller == null ||
			controller.fieldKey === key ||
			visiting.has(key)
		) {
			return true;
		}
		visiting.add(key);
		const answer = Object.prototype.hasOwnProperty.call(
			answers,
			controller.fieldKey,
		)
			? answers[controller.fieldKey]
			: undefined;
		const result =
			isShown(controller.fieldKey) &&
			formShowWhenMatches(rule, controller, answer);
		visiting.delete(key);
		shown.set(key, result);
		return result;
	};

	const hidden = new Set<string>();
	for (const field of fields) {
		if (!isShown(field.fieldKey)) {
			hidden.add(field.fieldKey);
		}
	}
	return hidden;
}

function nameOf(
	field: Pick<FormFieldDefinitionModel, 'fieldKey' | 'label' | 'adminLabel'>,
): string {
	const name = field.adminLabel ?? field.label;
	return name.trim() !== '' ? name.trim() : field.fieldKey;
}

/**
 * The checks on one question's rule (run by checkFormDefinition, path
 * `fields[i].showWhen`): not on a hidden value, not on itself, the question it names is
 * on the form, comes earlier, is a dropdown, multiple-choice or yes/no question, and the
 * answers are at least one of its own. Exported so the field editor shows the same
 * problem the server would refuse.
 */
export function checkFormFieldShowWhen(
	fields: ReadonlyArray<
		Pick<
			FormFieldDefinitionModel,
			| 'fieldKey'
			| 'label'
			| 'adminLabel'
			| 'fieldType'
			| 'selectOptions'
			| 'showWhen'
		>
	>,
	index: number,
	at = `fields[${index}].showWhen`,
): Array<FormDefinitionProblemModel> {
	const field = fields[index];
	const rule = field?.showWhen ?? null;
	if (field == null || rule == null) {
		return [];
	}
	const problem = (message: string): Array<FormDefinitionProblemModel> => [
		{ path: at, message },
	];

	if (field.fieldType === 'hidden') {
		return problem(
			'A hidden value is never shown, so it cannot depend on an answer',
		);
	}
	if (rule.fieldKey === field.fieldKey) {
		return problem('A question cannot depend on its own answer');
	}
	const controllerIndex = fields.findIndex(
		(candidate) => candidate.fieldKey === rule.fieldKey,
	);
	const controller = fields[controllerIndex];
	if (controller == null) {
		return problem(
			`Show only when names ${rule.fieldKey}, which is not a question on this form`,
		);
	}
	if (controllerIndex > index) {
		return problem(
			`"${nameOf(controller)}" comes after this question; a question can depend only on one before it`,
		);
	}
	if (!isFormShowWhenController(controller)) {
		return problem(
			`"${nameOf(controller)}" cannot show or hide questions; only a dropdown, multiple-choice or yes/no question can`,
		);
	}
	if (rule.values.length === 0) {
		return problem('Choose at least one answer that shows this question');
	}
	const offered = new Set(
		formShowWhenAnswers(controller).map((answer) => answer.value),
	);
	const unknown = rule.values.filter((value) => !offered.has(value));
	if (unknown.length > 0) {
		return problem(
			`"${nameOf(controller)}" has no answer ${unknown.map((value) => `"${value}"`).join(', ')}`,
		);
	}
	return [];
}
